/* eslint-disable max-lines */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { find, isEmpty, map, omit } from 'lodash';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';

import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import SelectField from 'common/forms/SelectField';
import { formatZipCode } from 'common/utils';
import { validateCombinedAddressLength, validateZipCode } from 'common/forms/formValidations';
import TextField from 'common/forms/TextField';
import { addPatientAddress } from 'store/thunks/patientThunks';
import CheckboxField from 'common/forms/CheckboxField';
import RadioGroupField from 'common/forms/RadioGroupField';
import { patientActions } from 'store/slices/patientSlice';
import PatientBanner from 'common/modules/PatientBanner';
import useSmsResetWarningDialog from './useSmsResetWarningDialog';

const NewPatientAddressButton = ({ mpi, children, disabled, showDeliveryInstructions }) => {
  const { InfoDialog, showWarning } = useSmsResetWarningDialog();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const patientInfo = useSelector(({ patient }) => patient[mpi]);
  const patientName = patientInfo?.firstName;
  const notificationPreference = patientInfo?.notificationPreferences;
  const [loading, setLoading] = useState(false);
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [inputAddress, setInputAddress] = useState({});
  const { enqueueSnackbar } = useSnackbar();

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
      setInputAddress({});
      setSuggestedAddresses([]);
    }
  };

  const closeDialog = () => {
    setLoading(false);
    setSuggestedAddresses([]);
    setInputAddress(inputAddress);
  };

  const dispatchAddPatientAddress = (formData) => {
    setInputAddress(formData);
    setLoading(true);

    return dispatch(
      addPatientAddress({
        mpi,
        address: formData,
      })
    )
      .then((response) => {
        if (Array.isArray(response) && !isEmpty(response)) {
          setSuggestedAddresses(response);
          return;
        }

        enqueueSnackbar({ variant: 'success', message: 'Patient address saved successfully.' });
        closeDialog();
        handleCloseModal();
      })
      .catch(() => {
        setSuggestedAddresses([
          {
            line1: formData.addressLine1,
            line2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
          },
        ]);
      })
      .finally(() => setLoading(false));
  };

  const resetPhonePreference = () => {
    dispatch(
      patientActions.updatePatientNotificationPreference({
        phone: false,
        email: notificationPreference?.email,
        mpi,
      })
    );
  };

  const handleAddPatientAddress = (formData) => {
    const address = { ...formData, isValidationRequired: true };

    const isDisallowedState = !find(usStateAbbreviation, {
      abbreviation: address.state,
    })?.isSMSAllowed;

    showWarning({
      warn: address.isDefault && isDisallowedState && notificationPreference?.phone,
      callback: () => dispatchAddPatientAddress(address),
      warnCallback: () => {
        resetPhonePreference();
        return dispatchAddPatientAddress(address);
      },
    });
  };

  const handleConfirmAddress = (formData) => {
    const selectedAddress = formData?.isManuallyVerified
      ? suggestedAddresses[0]
      : suggestedAddresses[formData.addressChoice];

    const addressToSave = {
      ...omit(selectedAddress, ['line1', 'line2']),
      addressLine1: selectedAddress.line1,
      addressLine2: selectedAddress.line2,
      nameOnAddress: inputAddress.nameOnAddress,
      isDefault: inputAddress.isDefault,
      isValidationRequired: false,
      isManuallyVerified: formData?.isManuallyVerified,
      deliveryInstructions: inputAddress.deliveryInstructions || undefined,
    };

    return dispatchAddPatientAddress(addressToSave);
  };

  return (
    <>
      <Button variant='contained' disabled={!patientName || disabled} onClick={handleOpenModal}>
        {children || 'New'}
      </Button>

      <InfoDialog />

      <Dialog
        open={showModal}
        aria-describedby='PatientView-NewPatientAddressButton-header'
        fullWidth
      >
        <DialogTitle id='PatientView-NewPatientAddressButton-header' sx={{ pb: 0 }}>
          <PatientBanner mpi={mpi} />
        </DialogTitle>

        {!isEmpty(suggestedAddresses) ? (
          <Form
            onSubmit={handleConfirmAddress}
            initialValues={{ addressChoice: '0' }}
            render={({ handleSubmit, invalid }) => (
              <form noValidate onSubmit={handleSubmit}>
                {suggestedAddresses.length > 1 ? (
                  <DialogContent>
                    <Typography variant='h6' sx={{ p: 0, pb: 0 }}>
                      We noticed that the address you entered may not be valid. Please select one
                      from the suggestions below.
                    </Typography>

                    <RadioGroupField
                      name='addressChoice'
                      id='PatientView-NewPatientAddressButton-selectAddress-input'
                      label=''
                    >
                      <Stack direction='column' alignItems='flex-start'>
                        {map(suggestedAddresses, (address, index) => (
                          <FormControlLabel
                            key={`validateAddress-${index}`}
                            value={index}
                            control={<Radio sx={{ mr: 1 }} />}
                            label={
                              <ListItemText
                                primary={
                                  <>
                                    <Typography variant='h6'>{address.line1}</Typography>
                                    {address?.line2 && <Typography>{address.line2}</Typography>}
                                    <Typography>
                                      {address.city} , {address.state}, {address.zip}
                                    </Typography>
                                  </>
                                }
                              />
                            }
                          />
                        ))}
                      </Stack>
                    </RadioGroupField>
                  </DialogContent>
                ) : (
                  <DialogContent>
                    <Typography sx={{ fontSize: '15px', p: 0, pb: 0 }}>
                      The Address You Entered Couldn’t Be Verified. You Can Still Choose To Save It
                      As Entered.
                    </Typography>
                    {map(suggestedAddresses, (address, index) => (
                      <CheckboxField
                        sx={{ pb: 0 }}
                        required
                        key={index}
                        id='PatientView-NewPatientAddressButton-isManuallyVerified-checkbox'
                        name='isManuallyVerified'
                        label={
                          <Typography fontSize='15px'>{`${address.line1}, ${
                            address.line2 ? `${address.line2}, ` : ''
                          }${address.city}, ${address.state} ${address.zip}`}</Typography>
                        }
                      />
                    ))}
                    <Typography sx={{ fontSize: '15px', p: 0, pb: 0 }}>
                      Would You Like To Continue?
                    </Typography>
                  </DialogContent>
                )}
                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={closeDialog}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    variant='contained'
                    type='submit'
                    disabled={invalid}
                  >
                    Confirm
                  </LoadingButton>
                </DialogActions>
              </form>
            )}
          />
        ) : (
          <Form
            onSubmit={handleAddPatientAddress}
            initialValues={inputAddress}
            render={({ handleSubmit, invalid }) => (
              <form noValidate onSubmit={handleSubmit}>
                <DialogContent>
                  <TextField
                    id='PatientView-NewPatientAddressButton-nameOnAddress-input'
                    label='Name On Address'
                    name='nameOnAddress'
                    required
                    autoFocus
                  />
                  <TextField
                    id='PatientView-NewPatientAddressButton-addressLine1-input'
                    label='Address Line 1'
                    name='addressLine1'
                    required
                    validations={[validateCombinedAddressLength()]}
                  />
                  <TextField
                    id='PatientView-NewPatientAddressButton-addressLine2-input'
                    label='Address Line 2'
                    name='addressLine2'
                    validations={[validateCombinedAddressLength()]}
                  />
                  <TextField
                    id='PatientView-NewPatientAddressButton-city-input'
                    label='City'
                    name='city'
                    required
                  />

                  <SelectField
                    id='PatientView-NewPatientAddressButton-state-dropdown'
                    label='State'
                    name='state'
                    required
                  >
                    {map(usStateAbbreviation, ({ name, abbreviation }) => (
                      <MenuItem value={abbreviation} key={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                  <TextField
                    id='PatientView-NewPatientAddressButton-zip-input'
                    label='Zip'
                    name='zip'
                    type='zip'
                    required
                    maxLength={10}
                    format={formatZipCode}
                    validations={[validateZipCode()]}
                  />
                  {showDeliveryInstructions && (
                    <TextField
                      id='PatientView-NewPatientAddressButton-deliveryInstructions-input'
                      label='Delivery Instructions'
                      name='deliveryInstructions'
                      maxLength={200}
                      multiline
                      rows={2}
                    />
                  )}
                  <CheckboxField
                    label='Default'
                    name='isDefault'
                    id='PatientView-NewPatientAddressButton-isDefault-checkbox'
                  />
                </DialogContent>

                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    variant='contained'
                    disabled={invalid}
                    type='submit'
                  >
                    Save
                  </LoadingButton>
                </DialogActions>
              </form>
            )}
          />
        )}
      </Dialog>
    </>
  );
};

NewPatientAddressButton.propTypes = {
  mpi: PropTypes.string.isRequired,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  showDeliveryInstructions: PropTypes.bool,
};

NewPatientAddressButton.defaultProps = {
  children: 'New',
  disabled: false,
  showDeliveryInstructions: true,
};

export default NewPatientAddressButton;
